import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '@kss/api';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { ArrowLeft } from 'lucide-react';
import { EntityPicker } from '../sales/EntityPicker';

/**
 * Focused Quotation entry screen (issue ⑥).
 *
 * The registry form rendered `convertedSalesOrderId` as a bare number input
 * and couldn't auto-fill. Here the source Sales Order is a searchable
 * dropdown; picking one sets `convertedSalesOrderId`, pulls the customer +
 * totals from the order, and copies its lines into the quotation. Saved via
 * the real schema columns (numeric `status`, string `quotationStatus`) plus
 * real `QuotationLine` rows.
 */

interface QLine {
  key: string;
  productId: number | string | null;
  productName: string;
  quantity: number;
  unitOfMeasure: string;
  unitPrice: number;
  taxRate: number;
  lineTotal: number;
}

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
const rid = () => Math.random().toString(36).slice(2);
const todayISODate = () => new Date().toISOString().slice(0, 10);

export default function QuotationComposer() {
  const { id } = useParams();
  const isEdit = !!id && id !== 'new';
  const navigate = useNavigate();
  const toast = useToast();

  const [quotationNumber, setQuotationNumber] = useState(
    isEdit ? '' : `QTN-${Date.now().toString(36).toUpperCase().slice(-6)}`,
  );
  const [quotationDate, setQuotationDate] = useState(todayISODate());
  const [validUntil, setValidUntil] = useState('');
  const [customerId, setCustomerId] = useState<number | string | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [convertedSalesOrderId, setConvertedSalesOrderId] = useState<number | string | null>(null);
  const [salesOrderLabel, setSalesOrderLabel] = useState('');
  const [subTotal, setSubTotal] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [quotationStatus, setQuotationStatus] = useState('Draft');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<QLine[]>([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // ---- Edit hydrate -------------------------------------------------------
  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    void (async () => {
      try {
        const r = await apiClient.get('/crm/Quotation');
        const d = r.data;
        const rows = (Array.isArray(d) ? d : (d?.items ?? d?.data ?? [])) as Array<Record<string, unknown>>;
        const row = rows.find((x) => String(x.code ?? '') === String(id) || String(x.id ?? '') === String(id));
        if (!row || cancelled) return;
        setQuotationNumber(String(row.quotationNumber ?? row.code ?? id));
        if (row.quotationDate) setQuotationDate(String(row.quotationDate).slice(0, 10));
        if (row.validUntil) setValidUntil(String(row.validUntil).slice(0, 10));
        if (row.customerId != null) setCustomerId(row.customerId as number);
        setCustomerName(String(row.customerName ?? ''));
        if (row.convertedSalesOrderId != null) setConvertedSalesOrderId(row.convertedSalesOrderId as number);
        setSubTotal(Number(row.subTotal ?? 0));
        setDiscountAmount(Number(row.discountAmount ?? 0));
        setTaxAmount(Number(row.taxAmount ?? 0));
        setGrandTotal(Number(row.grandTotal ?? 0));
        if (row.quotationStatus) setQuotationStatus(String(row.quotationStatus));
        setNotes(String(row.notes ?? ''));
        // lines
        const lr = await apiClient.get('/crm/QuotationLine');
        const ld = lr.data;
        const lrows = (Array.isArray(ld) ? ld : (ld?.items ?? ld?.data ?? [])) as Array<Record<string, unknown>>;
        const mine = lrows
          .filter((x) => String(x.quotationId) === String(row.id))
          .sort((a, b) => Number(a.lineNumber ?? 0) - Number(b.lineNumber ?? 0));
        if (mine.length && !cancelled) {
          setLines(mine.map((l) => ({
            key: rid(),
            productId: (l.productId ?? null) as number | null,
            productName: String(l.productName ?? l.name ?? ''),
            quantity: Number(l.quantity ?? 1) || 1,
            unitOfMeasure: String(l.unitOfMeasure ?? 'PCS'),
            unitPrice: Number(l.unitPrice ?? 0),
            taxRate: Number(l.taxRate ?? 0),
            lineTotal: Number(l.lineTotal ?? 0),
          })));
        }
      } catch { /* leave blank */ }
    })();
    return () => { cancelled = true; };
  }, [isEdit, id]);

  // ---- Auto-fill from a picked Sales Order --------------------------------
  async function pickSalesOrder(so: Record<string, unknown> | null) {
    if (!so) {
      setConvertedSalesOrderId(null);
      setSalesOrderLabel('');
      return;
    }
    const soId = (so.id ?? so.salesOrderId ?? null) as number | null;
    setConvertedSalesOrderId(soId);
    setSalesOrderLabel(String(so.orderNumber ?? so.code ?? `#${soId}`));
    if (so.customerId != null) setCustomerId(so.customerId as number);
    if (so.customerName) setCustomerName(String(so.customerName));
    setSubTotal(Number(so.subTotal ?? 0));
    setDiscountAmount(Number(so.discountAmount ?? 0));
    setTaxAmount(Number(so.taxAmount ?? 0));
    setGrandTotal(Number(so.grandTotal ?? 0));
    // Copy the order's lines into the quotation.
    try {
      const lr = await apiClient.get('/sales/SalesOrderLine');
      const ld = lr.data;
      const lrows = (Array.isArray(ld) ? ld : (ld?.items ?? ld?.data ?? [])) as Array<Record<string, unknown>>;
      const mine = lrows
        .filter((x) => String(x.salesOrderId) === String(soId))
        .sort((a, b) => Number(a.lineNumber ?? 0) - Number(b.lineNumber ?? 0));
      setLines(mine.map((l) => ({
        key: rid(),
        productId: (l.productId ?? null) as number | null,
        productName: String(l.productName ?? l.name ?? ''),
        quantity: Number(l.quantity ?? 1) || 1,
        unitOfMeasure: String(l.unitOfMeasure ?? 'PCS'),
        unitPrice: Number(l.unitPrice ?? 0),
        taxRate: Number(l.taxRate ?? 0),
        lineTotal: Number(l.lineTotal ?? (Number(l.quantity ?? 0) * Number(l.unitPrice ?? 0))),
      })));
      toast.success('Auto-filled', `Pulled ${mine.length} line(s) + totals from ${String(so.orderNumber ?? so.code ?? '')}`);
    } catch {
      toast.error('Line copy failed', 'Totals filled, but could not load the order lines.');
    }
  }

  const computedGrand = useMemo(
    () => (grandTotal > 0 ? grandTotal : round2(subTotal - discountAmount + taxAmount)),
    [grandTotal, subTotal, discountAmount, taxAmount],
  );

  async function save() {
    if (!customerId) { setErr('Select a customer (or a Sales Order that has one).'); return; }
    setSaving(true); setErr(null);
    try {
      const payload: Record<string, unknown> = {
        code: quotationNumber,
        name: `Quotation ${quotationNumber}`,
        status: 0,
        quotationNumber,
        quotationDate: quotationDate ? new Date(quotationDate).toISOString() : new Date().toISOString(),
        validUntil: validUntil ? new Date(validUntil).toISOString() : null,
        customerId: customerId ? Number(customerId) : null,
        customerName,
        convertedSalesOrderId: convertedSalesOrderId ? Number(convertedSalesOrderId) : null,
        currency: 'INR',
        subTotal: round2(subTotal),
        discountAmount: round2(discountAmount),
        taxAmount: round2(taxAmount),
        grandTotal: round2(computedGrand),
        quotationStatus,
        notes: notes || undefined,
      };
      let qId: number | string | undefined;
      if (isEdit) {
        const r = await apiClient.put('/crm/Quotation', { ...payload, code: quotationNumber });
        qId = ((r.data?.data ?? r.data) as Record<string, unknown>)?.id as number;
      } else {
        const r = await apiClient.post('/crm/Quotation', payload);
        qId = ((r.data?.data ?? r.data) as Record<string, unknown>)?.id as number;
      }
      // Lines → QuotationLine.
      if (qId != null && lines.length) {
        if (isEdit) {
          try {
            const ex = await apiClient.get('/crm/QuotationLine');
            const exRows = (Array.isArray(ex.data) ? ex.data : (ex.data?.items ?? ex.data?.data ?? [])) as Array<Record<string, unknown>>;
            await Promise.all(
              exRows.filter((row) => String(row.quotationId) === String(qId))
                .map((row) => apiClient.delete(`/crm/QuotationLine/${row.id}`).catch(() => undefined)),
            );
          } catch { /* best effort */ }
        }
        await Promise.all(lines.map((l, i) => apiClient.post('/crm/QuotationLine', {
          code: `${quotationNumber}-L${i + 1}`,
          name: l.productName || `Line ${i + 1}`,
          status: 0,
          quotationId: qId,
          lineNumber: i + 1,
          productId: l.productId ?? null,
          productName: l.productName,
          quantity: Number(l.quantity) || 0,
          unitOfMeasure: l.unitOfMeasure || 'PCS',
          unitPrice: Number(l.unitPrice) || 0,
          lineDiscount: 0,
          taxRate: Number(l.taxRate) || 0,
          lineTotal: round2(Number(l.quantity) * Number(l.unitPrice)),
        }).catch(() => undefined)));
      }
      toast.success('Saved', `Quotation ${quotationNumber} ${isEdit ? 'updated' : 'created'}`);
      navigate('/panel/api/crm/Quotation');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setErr(msg);
      toast.error('Save failed', msg);
    } finally {
      setSaving(false);
    }
  }

  const inputCls = 'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm outline-none focus:border-primary';

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-1">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate('/panel/api/crm/Quotation')} className="grid h-8 w-8 place-items-center rounded border border-border hover:border-primary" aria-label="Back">
          <ArrowLeft size={16} />
        </button>
        <h1 className="text-base font-semibold">{isEdit ? 'Edit' : 'New'} Quotation</h1>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <Card className="space-y-3 p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Quotation No">
            <input className={inputCls} value={quotationNumber} onChange={(e) => setQuotationNumber(e.target.value)} />
          </Field>
          <Field label="Quotation Date">
            <input type="date" className={inputCls} value={quotationDate} onChange={(e) => setQuotationDate(e.target.value)} />
          </Field>
          <Field label="Valid Until">
            <input type="date" className={inputCls} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
          </Field>
          <Field label="Customer">
            <EntityPicker<Record<string, unknown>>
              basePath="/crm/Customer" idKey="id" labelKey="name" secondaryKey="code"
              value={customerId}
              selectedRow={customerName ? ({ id: customerId, name: customerName } as Record<string, unknown>) : null}
              onChange={(c) => { setCustomerId((c?.id ?? c?.customerId ?? null) as number | null); setCustomerName(String(c?.name ?? '')); }}
              placeholder="Select customer"
            />
          </Field>
          <Field label="From Sales Order (auto-fills totals + lines)">
            <EntityPicker<Record<string, unknown>>
              basePath="/sales/SalesOrder" idKey="id" labelKey="orderNumber" secondaryKey="code"
              value={convertedSalesOrderId}
              selectedRow={salesOrderLabel ? ({ id: convertedSalesOrderId, orderNumber: salesOrderLabel } as Record<string, unknown>) : null}
              onChange={(so) => void pickSalesOrder(so)}
              placeholder="Select sales order"
            />
          </Field>
          <Field label="Status">
            <select className={inputCls} value={quotationStatus} onChange={(e) => setQuotationStatus(e.target.value)}>
              {['Draft', 'Sent', 'Accepted', 'Rejected', 'Expired'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>
      </Card>

      <Card className="p-0">
        <div className="border-b border-border px-4 py-2 text-sm font-medium">Line items {lines.length ? `(${lines.length})` : ''}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-card-hover/40 text-left text-[11px] text-content-muted">
              <tr><th className="px-3 py-2">#</th><th className="px-3 py-2">Product</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">GST%</th><th className="px-3 py-2 text-right">Total</th></tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-4 text-center text-content-muted">Pick a Sales Order above to pull its items.</td></tr>
              ) : lines.map((l, i) => (
                <tr key={l.key} className="border-t border-border">
                  <td className="px-3 py-1.5 text-content-muted">{i + 1}</td>
                  <td className="px-3 py-1.5">{l.productName || '—'}</td>
                  <td className="px-3 py-1.5 text-right">{l.quantity}</td>
                  <td className="px-3 py-1.5 text-right">₹{l.unitPrice.toFixed(2)}</td>
                  <td className="px-3 py-1.5 text-right">{l.taxRate}%</td>
                  <td className="px-3 py-1.5 text-right">₹{round2(l.quantity * l.unitPrice).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="space-y-2 p-4">
        <Row label="Sub Total" value={subTotal} onChange={setSubTotal} />
        <Row label="Discount" value={discountAmount} onChange={setDiscountAmount} />
        <Row label="Tax" value={taxAmount} onChange={setTaxAmount} />
        <div className="flex items-center justify-between border-t border-border pt-2 text-sm font-semibold">
          <span>Grand Total</span><span>₹ {computedGrand.toFixed(2)}</span>
        </div>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/panel/api/crm/Quotation')} disabled={saving}>Cancel</Button>
        <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Quotation'}</Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-content-secondary">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-content-secondary">{label}</span>
      <input
        type="number"
        className="h-8 w-32 rounded border border-border bg-surface-card px-2 text-right outline-none focus:border-primary"
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      />
    </div>
  );
}
